import React, { useState, useRef, useEffect, useCallback } from "react";
import { ReactComponent as DotIcon } from "images/dots.svg";
import "./splitPanel.scss";

export type SplitPanelSection = {
  key: string;
  content: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
};

type SplitPanelProps = {
  sections: SplitPanelSection[];
  initialWidths?: number[];
  minSectionWidth?: number;
};

type DragState = {
  dividerIndex: number;
  startX: number;
  startWidths: number[];
};

const normalizeWidths = (count: number, inputWidths?: number[]) => {
  if (!inputWidths || inputWidths.length !== count || count === 0) {
    const evenWidth = 100 / Math.max(count, 1);
    return Array.from({ length: count }, () => evenWidth);
  }

  const total = inputWidths.reduce((sum, width) => sum + width, 0);
  if (total <= 0) {
    const evenWidth = 100 / count;
    return Array.from({ length: count }, () => evenWidth);
  }

  return inputWidths.map((width) => (width / total) * 100);
};

const SplitPanel: React.FC<SplitPanelProps> = ({
  sections,
  initialWidths,
  minSectionWidth = 15,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [sectionWidths, setSectionWidths] = useState<number[]>(
    normalizeWidths(sections.length, initialWidths)
  );
  const dragging = useRef<DragState | null>(null);

  // `initialWidths` is often passed inline (new array each render); depend on values, not reference.
  const initialWidthsKey =
    initialWidths != null && initialWidths.length > 0 ? initialWidths.join(",") : "";

  useEffect(() => {
    const widthsFromKey =
      initialWidthsKey.length > 0
        ? initialWidthsKey.split(",").map(Number)
        : undefined;
    setSectionWidths(normalizeWidths(sections.length, widthsFromKey));
  }, [sections.length, initialWidthsKey]);

  const handleMouseMove = useCallback(
    (event: MouseEvent) => {
      if (!dragging.current || !containerRef.current) return;

      const { dividerIndex, startX, startWidths } = dragging.current;
      const rect = containerRef.current.getBoundingClientRect();
      if (!rect.width) return;

      const deltaInPercent = ((event.clientX - startX) / rect.width) * 100;
      const leftStart = startWidths[dividerIndex];
      const rightStart = startWidths[dividerIndex + 1];
      const pairTotal = leftStart + rightStart;

      const minLeft = minSectionWidth;
      const maxLeft = pairTotal - minSectionWidth;
      const nextLeft = Math.max(
        minLeft,
        Math.min(leftStart + deltaInPercent, maxLeft)
      );
      const nextRight = pairTotal - nextLeft;

      setSectionWidths((previous) => {
        const next = [...previous];
        next[dividerIndex] = nextLeft;
        next[dividerIndex + 1] = nextRight;
        return next;
      });
    },
    [minSectionWidth]
  );

  const stopDragging = useCallback(() => {
    dragging.current = null;
    document.body.style.cursor = "";
    document.body.style.userSelect = "";
  }, []);

  useEffect(() => {
    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", stopDragging);
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", stopDragging);
    };
  }, [handleMouseMove, stopDragging]);

  const startDragging = (dividerIndex: number, clientX: number) => {
    dragging.current = {
      dividerIndex,
      startX: clientX,
      startWidths: [...sectionWidths],
    };
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
  };

  if (sections.length === 0) {
    return null;
  }

  return (
    <div ref={containerRef} className="split-panel-container">
      {sections.map((section, index) => {
        const width = sectionWidths[index] || 100 / sections.length;
        const isLast = index === sections.length - 1;

        return (
          <React.Fragment key={section.key}>
         
            <div
              className={`split-panel-section ${section.className || ""}`.trim()}
              style={{ width: `${width}%`, ...section.style }}
            >
              {section.content}
            </div>
            {!isLast && (
              <div
                className="split-divider"
                onMouseDown={(event) => startDragging(index, event.clientX)}
              >
                <div className="split-icon">
                  <DotIcon />
                </div>
              </div>
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
};

export default SplitPanel;
